import React, { useState } from "react";
import { BidVersion } from "../types";
import { formatCurrency, cn } from "../utils";
import LZString from "lz-string";
import { Clock, RotateCcw, CheckCircle2, Trash2, ArrowUpDown } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { SectionPillHeader } from "./SectionPillHeader";

interface Props {
  versions: BidVersion[];
  onRevert: (version: BidVersion) => void;
  onDelete?: (versionId: string) => void;
  readOnly?: boolean;
  activeVersionId?: string | null;
  currentWorkingVersionCode?: string | null;
  currentWorkingTotalCost?: number | null;
}

export function VersionHistory({
  versions,
  onRevert,
  onDelete,
  readOnly = false,
  activeVersionId,
  currentWorkingVersionCode,
  currentWorkingTotalCost,
}: Props) {
  const { user, login } = useAuth();
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');

  if (!user) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-4 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <Clock className="mx-auto mb-3 h-8 w-8 text-slate-300 dark:text-slate-600" />
        <h3 className="mb-2 text-sm font-bold text-slate-800 dark:text-slate-100">Version History</h3>
        <p className="mb-4 text-xs text-slate-500 dark:text-slate-400">
          Sign in to enable cloud saving and automatic version history.
        </p>
        <button
          onClick={login}
          className="w-full rounded-lg bg-indigo-600 py-2 text-xs font-bold text-white transition-colors hover:bg-indigo-700"
        >
          Sign In
        </button>
      </div>
    );
  }

  const sortedVersions = [...versions].sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });

  const hasLockedHistory = sortedVersions.some((version) => version.locked);
  const displayVersions = !hasLockedHistory && currentWorkingVersionCode
    ? sortedVersions.filter((version) => {
        const versionCode = version.versionCode || version.versionName;
        return versionCode === currentWorkingVersionCode;
      })
    : sortedVersions;

  const hasWorkingVersionRecord = displayVersions.some((version) => {
    const versionCode = version.versionCode || version.versionName;
    return Boolean(currentWorkingVersionCode) && versionCode === currentWorkingVersionCode && !version.locked;
  });

  const showSyntheticWorkingVersion = Boolean(currentWorkingVersionCode) && !hasWorkingVersionRecord;
  const syntheticCurrency = displayVersions[0]?.setup?.currency || sortedVersions[0]?.setup?.currency || "GBP";

  if (displayVersions.length === 0 && !showSyntheticWorkingVersion) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-4 text-center text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
        No versions saved yet.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="mb-4 flex min-h-[32px] items-center justify-between">
        <SectionPillHeader
          title="Version History"
          icon={Clock}
        />
        <button 
          onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
          className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          title={`Sort ${sortOrder === 'desc' ? 'Oldest First' : 'Newest First'}`}
        >
          <ArrowUpDown className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="max-h-[400px] overflow-auto divide-y divide-slate-100 dark:divide-slate-800">
        {showSyntheticWorkingVersion && (
          <div className="group relative p-4 bg-indigo-50/50 hover:bg-indigo-50 dark:bg-indigo-950/30 dark:hover:bg-indigo-950/40">
            <div className="flex justify-between items-start mb-1">
              <div className="flex items-center gap-2">
                <span className="text-[10pt] font-bold text-slate-900 dark:text-slate-100">{currentWorkingVersionCode}</span>
                <span className="inline-flex items-center gap-1 rounded bg-indigo-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">
                  <CheckCircle2 className="w-2.5 h-2.5" />
                  Working
                </span>
              </div>
              <div className="text-[10pt] font-bold text-indigo-600 dark:text-indigo-400">
                {formatCurrency(currentWorkingTotalCost || 0, syntheticCurrency)}
              </div>
            </div>

            <div className="flex justify-between items-end">
              <div className="text-[10px] text-slate-500 dark:text-slate-400">
                <div>Current draft</div>
                <div>Not yet exported to version history</div>
              </div>
            </div>
          </div>
        )}
        {displayVersions.map((version) => {
          const versionCode = version.versionCode || version.versionName;
          const isWorkingVersion = !version.locked && Boolean(currentWorkingVersionCode) && versionCode === currentWorkingVersionCode;
          const isActive = !showSyntheticWorkingVersion && (version.id === activeVersionId || isWorkingVersion);
          
          let totalCost = version.totalCost;

          if (isWorkingVersion && currentWorkingTotalCost !== null && currentWorkingTotalCost !== undefined) {
            totalCost = currentWorkingTotalCost;
          }
          
          if (totalCost === undefined) {
            let shotsToCalculate = version.shots || [];
            let assetsToCalculate = version.assets || [];

            if (shotsToCalculate.length === 0 && version.compressedShots) {
              try {
                shotsToCalculate = JSON.parse(LZString.decompressFromUTF16(version.compressedShots) || "[]");
              } catch (e) {
                console.error("Failed to decompress shots", e);
              }
            }

            if (assetsToCalculate.length === 0 && version.compressedAssets) {
              try {
                assetsToCalculate = JSON.parse(LZString.decompressFromUTF16(version.compressedAssets) || "[]");
              } catch (e) {
                console.error("Failed to decompress assets", e);
              }
            }

            const shotsCost = shotsToCalculate
              .filter((s) => s.active)
              .reduce((acc, shot) => {
                const baseTaskCost =
                  (shot.days.concept || 0) * (version.rates.concept || 0) +
                  (shot.days.dmp || 0) * (version.rates.dmp || 0) +
                  (shot.days.comp || 0) * (version.rates.comp || 0) +
                  (shot.days.roto || 0) * (version.rates.roto || 0) +
                  (shot.days.prep || 0) * (version.rates.prep || 0) +
                  (shot.days.gfx || 0) * (version.rates.gfx || 0) +
                  (shot.days.cameraTR || 0) * (version.rates.cameraTR || 0) +
                  (shot.days.objectTR || 0) * (version.rates.objectTR || 0) +
                  (shot.days.rANIM || 0) * (version.rates.rANIM || 0) +
                  (shot.days.layout || 0) * (version.rates.layout || 0) +
                  (shot.days.anim || 0) * (version.rates.anim || 0) +
                  (shot.days.light || 0) * (version.rates.light || 0) +
                  (shot.days.fx || 0) * (version.rates.fx || 0);
                
                const taskDays = 
                  (shot.days.concept || 0) +
                  (shot.days.dmp || 0) +
                  (shot.days.comp || 0) +
                  (shot.days.roto || 0) +
                  (shot.days.prep || 0) +
                  (shot.days.gfx || 0) +
                  (shot.days.cameraTR || 0) +
                  (shot.days.objectTR || 0) +
                  (shot.days.rANIM || 0) +
                  (shot.days.layout || 0) +
                  (shot.days.anim || 0) +
                  (shot.days.light || 0) +
                  (shot.days.fx || 0);
                
                const supDays = taskDays * ((version.setup.supervisionPercentage || 0) / 100);
                const supCost = supDays * (version.rates.supervisor || 0);
                const taskCost = baseTaskCost + supCost;
                return acc + taskCost * (shot.quantity || 1);
              }, 0);

            const assetsCost = assetsToCalculate
              .filter((a) => a.active)
              .reduce((acc, asset) => {
                const rates = version.assetRates || {
                  supervisor: 10, concept: 800, dmp: 800, model: 800, rig: 800,
                  retop: 800, cloth: 800, fur: 800, groom: 800, texture: 800,
                  anim: 800, fx: 850, lookdev: 800, photoscan: 800
                };
                const baseTaskCost =
                  (asset.days.concept || 0) * (rates.concept || 0) +
                  (asset.days.dmp || 0) * (rates.dmp || 0) +
                  (asset.days.model || 0) * (rates.model || 0) +
                  (asset.days.rig || 0) * (rates.rig || 0) +
                  (asset.days.retop || 0) * (rates.retop || 0) +
                  (asset.days.cloth || 0) * (rates.cloth || 0) +
                  (asset.days.fur || 0) * (rates.fur || 0) +
                  (asset.days.groom || 0) * (rates.groom || 0) +
                  (asset.days.texture || 0) * (rates.texture || 0) +
                  (asset.days.anim || 0) * (rates.anim || 0) +
                  (asset.days.fx || 0) * (rates.fx || 0) +
                  (asset.days.lookdev || 0) * (rates.lookdev || 0) +
                  (asset.days.photoscan || 0) * (rates.photoscan || 0);
                
                const taskDays = 
                  (asset.days.concept || 0) +
                  (asset.days.dmp || 0) +
                  (asset.days.model || 0) +
                  (asset.days.rig || 0) +
                  (asset.days.retop || 0) +
                  (asset.days.cloth || 0) +
                  (asset.days.fur || 0) +
                  (asset.days.groom || 0) +
                  (asset.days.texture || 0) +
                  (asset.days.anim || 0) +
                  (asset.days.fx || 0) +
                  (asset.days.lookdev || 0) +
                  (asset.days.photoscan || 0);
                
                const supDays = taskDays * ((version.setup.supervisionPercentage || 0) / 100);
                const supCost = supDays * (rates.supervisor || 0);
                const taskCost = baseTaskCost + supCost;
                return acc + taskCost * (asset.quantity || 1);
              }, 0);

            totalCost = shotsCost + assetsCost;
          }

          return (
            <div
              key={version.id}
              className={cn(
                "group relative p-4 hover:bg-slate-50 dark:hover:bg-slate-800/70",
                isActive && "bg-indigo-50/50 hover:bg-indigo-50 dark:bg-indigo-950/30 dark:hover:bg-indigo-950/40"
              )}
            >
              <div className="flex justify-between items-start mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10pt] font-bold text-slate-900 dark:text-slate-100">{version.versionName || version.versionCode}</span>
                  {isActive && (
                    <span className="inline-flex items-center gap-1 rounded bg-indigo-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">
                      <CheckCircle2 className="w-2.5 h-2.5" />
                      {isWorkingVersion ? "Working" : "Active"}
                    </span>
                  )}
                  {version.locked && (
                    <span className="inline-flex items-center rounded bg-slate-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                      Locked
                    </span>
                  )}
                </div>
                <div className="text-[10pt] font-bold text-indigo-600 dark:text-indigo-400">
                  {formatCurrency(totalCost, version.setup.currency)}
                </div>
              </div>
              
              <div className="flex justify-between items-end">
                <div className="text-[10px] text-slate-500 dark:text-slate-400">
                  <div>{new Date(version.createdAt).toLocaleString()}</div>
                  <div>by {version.createdBy}</div>
                </div>
                
                <div className="flex flex-col items-end gap-1">
                  {version.setup.targetCurrency && version.setup.exchangeRate && (
                    <div className="text-[10px] font-medium text-slate-500 dark:text-slate-400">
                      {formatCurrency(totalCost * version.setup.exchangeRate, version.setup.targetCurrency)}
                    </div>
                  )}
                  
                  {!readOnly && !isActive && !version.locked && (
                    <div className="flex items-center gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => onRevert(version)}
                        className="flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-[10px] font-bold text-slate-700 shadow-sm transition-all hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                        title="Revert to this version"
                      >
                        <RotateCcw className="w-3 h-3" />
                        Revert
                      </button>
                      {onDelete && (
                        <button
                          onClick={() => onDelete(version.id)}
                          className="rounded p-1.5 text-slate-400 transition-all hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30 dark:hover:text-red-400"
                          title="Delete version"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
